import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { ApiError, customInstance } from "@repo/api-client";
import { z } from "zod";
import type { PublicBookingChannel } from "../channel";
import type { BookingMode } from "../config/types";

/**
 * The typed client for the ANONYMOUS public booking endpoints (diner-booking-api, all under
 * `/api/public/v1/venues/{venueId}/…`). Every call funnels through the ADR-0020 fetch mutator
 * (`customInstance`) — uncredentialed, no token provider is ever set in this app — and validates the
 * response with zod, exactly like `config/useVenueConfig.ts`. The widget computes NO slot math: slots
 * and alternatives are rendered precisely as returned (AV-6/AV-8); this module only shapes I/O.
 *
 * Codegen note: the committed OpenAPI predates these public paths, so — like the venue-config read —
 * they are hand-typed here against the landed controller wire shapes rather than Orval-generated. When
 * the backend spec is regenerated with the `publicV1` group, these can migrate to the generated hooks
 * with no call-site change (the customInstance transport is identical).
 */

// --- wire schemas (mirror the landed controller DTOs) -------------------------------------------

const slotSchema = z.object({
  serviceId: z.string(),
  startSec: z.number(),
  durationSec: z.number(),
  spotsOpen: z.number().nullable().optional(),
});
export type Slot = z.infer<typeof slotSchema>;

const openSlotsResponseSchema = z.object({
  venueId: z.string(),
  serviceDay: z.string(),
  partySize: z.number(),
  slots: z.array(slotSchema),
  /** Non-null when the WHOLE request is a typed rejection (§D.1); `slots` is then empty. */
  rejection: z.string().nullable().optional(),
});
export type OpenSlotsResponse = z.infer<typeof openSlotsResponseSchema>;

const alternativeSchema = z.object({
  serviceId: z.string(),
  serviceDay: z.string(),
  startSec: z.number(),
  durationSec: z.number(),
  spotsOpen: z.number().nullable().optional(),
});
export type Alternative = z.infer<typeof alternativeSchema>;

const groupSlotSchema = slotSchema.extend({
  bookingMode: z.enum(["INSTANT_CONFIRM", "REQUEST_TO_BOOK"]),
});
export type GroupSlot = z.infer<typeof groupSlotSchema>;

const groupVenueSchema = z.object({
  venueId: z.string(),
  displayName: z.string(),
  slots: z.array(groupSlotSchema),
});
export type GroupVenue = z.infer<typeof groupVenueSchema>;

const groupAvailabilitySchema = z.object({
  serviceDay: z.string(),
  partySize: z.number(),
  venues: z.array(groupVenueSchema),
});
export type GroupAvailability = z.infer<typeof groupAvailabilitySchema>;

const holdResponseSchema = z.object({
  holdId: z.string(),
  expiresAtSec: z.number(),
});
export type HoldResponse = z.infer<typeof holdResponseSchema>;

/**
 * The card-guarantee requirement the create endpoint returns when the chosen service requires a card
 * (card-fingerprint-no-show.md — `Service.requireCreditCard` evaluated on the write path). Present +
 * `required` means the booking is CREATED but held at `paymentStatus` NOT_PROVIDED: it cannot confirm
 * until the widget's SetupIntent card step succeeds. `feeMinor` + `cutoffAtSec` are the SC-1 snapshot the
 * notice displays and the later charge always uses; absent for a service that needs no guarantee.
 */
const guaranteeRequirementSchema = z.object({
  required: z.boolean(),
  feeMinor: z.number().int().nonnegative(),
  currency: z.string(),
  cutoffAtSec: z.number().nullable().optional(),
});
export type GuaranteeRequirement = z.infer<typeof guaranteeRequirementSchema>;

const bookingSubmitResponseSchema = z.object({
  bookingId: z.string(),
  externalBookingId: z.string(),
  status: z.string(),
  manageToken: z.string(),
  serviceId: z.string(),
  startSec: z.number(),
  durationSec: z.number(),
  partySize: z.number(),
  serviceDay: z.string(),
  contactFlagged: z.boolean(),
  holdExpiresAtSec: z.number().nullable().optional(),
  guarantee: guaranteeRequirementSchema.nullable().optional(),
});
export type BookingSubmitResponse = z.infer<typeof bookingSubmitResponseSchema>;

// --- request bodies ------------------------------------------------------------------------------

export interface PlaceHoldBody {
  serviceId: string;
  startSec: number;
  partySize: number;
}

export interface CreateBookingGuest {
  givenName: string;
  familyName?: string;
  phone?: string;
  email?: string;
}

export interface CreateBookingBody {
  serviceId: string;
  startSec: number;
  partySize: number;
  /** The pre-submit hold promoted atomically; null on the large-party request path (SC-5). */
  holdId: string | null;
  guest: CreateBookingGuest;
  termsAccepted: boolean;
  channel: PublicBookingChannel;
  zonePreference?: string | null;
  specialRequests?: string | null;
  allergyNote?: string | null;
  language?: string | null;
  // --- booking-abuse-protection: consent-free telemetry + the Turnstile resubmit token ---
  /** A Cloudflare Turnstile token on a RESUBMIT after a CHALLENGE_REQUIRED response. */
  challengeToken?: string;
  /** Form-mount→submit elapsed (ms) — a submission-speed signal (no identifier). */
  formFillMillis?: number;
  /** Honeypot field: hidden from humans; any value = a bot. */
  honeypot?: string;
  /** Interaction bit: at least one real focus/keydown happened on the form. */
  interacted?: boolean;
}

// --- transport -----------------------------------------------------------------------------------

const base = (venueId: string) => `/api/public/v1/venues/${encodeURIComponent(venueId)}`;

export async function fetchOpenSlots(
  venueId: string,
  serviceDay: string,
  partySize: number,
): Promise<OpenSlotsResponse> {
  const qs = new URLSearchParams({ serviceDay, partySize: String(partySize) });
  const body = await customInstance<unknown>(`${base(venueId)}/availability?${qs.toString()}`, {
    method: "GET",
  });
  return openSlotsResponseSchema.parse(body);
}

export async function fetchAlternatives(
  venueId: string,
  serviceDay: string,
  partySize: number,
  aroundSec?: number,
): Promise<Alternative[]> {
  const qs = new URLSearchParams({ serviceDay, partySize: String(partySize) });
  if (aroundSec !== undefined) qs.set("aroundSec", String(aroundSec));
  const body = await customInstance<unknown>(
    `${base(venueId)}/availability/alternatives?${qs.toString()}`,
    { method: "GET" },
  );
  return z.array(alternativeSchema).parse(body);
}

export async function fetchGroupAvailability(
  venueId: string,
  serviceDay: string,
  partySize: number,
): Promise<GroupAvailability> {
  const qs = new URLSearchParams({ serviceDay, partySize: String(partySize) });
  const body = await customInstance<unknown>(
    `${base(venueId)}/group-availability?${qs.toString()}`,
    { method: "GET" },
  );
  return groupAvailabilitySchema.parse(body);
}

export async function placeHold(venueId: string, body: PlaceHoldBody): Promise<HoldResponse> {
  const res = await customInstance<unknown>(`${base(venueId)}/holds`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  return holdResponseSchema.parse(res);
}

/**
 * Submit a booking. The `idempotencyKey` is the flow's ONE key per logical submission (BK-2/ADR-0025)
 * — reused across transport retries, reminted only after the diner edits — passed explicitly so the
 * mutator does NOT auto-mint a fresh one. CONFIRMED vs PENDING is read from the response `status`
 * (the API never returns CONFIRMED for a request-to-book submission).
 */
export async function createBooking(
  venueId: string,
  idempotencyKey: string,
  body: CreateBookingBody,
): Promise<BookingSubmitResponse> {
  const res = await customInstance<unknown>(`${base(venueId)}/bookings`, {
    method: "POST",
    headers: { "content-type": "application/json", "Idempotency-Key": idempotencyKey },
    body: JSON.stringify(body),
  });
  return bookingSubmitResponseSchema.parse(res);
}

// --- manage-booking (no-login self-service, BK-4/6/10/12) --------------------------------------

/**
 * The manage-page projection — booking FACTS + the server cutoff verdict ONLY. It deliberately carries
 * NO guest phone/email: the manage token is a bearer credential, so a forwarded link must never expose
 * contact PII (GDPR data minimization). Times are epoch-seconds rendered venue-local by the UI.
 */
const manageBookingSchema = z.object({
  bookingId: z.string(),
  externalBookingId: z.string(),
  venueId: z.string(),
  venueName: z.string(),
  timeZone: z.string(),
  serviceId: z.string(),
  status: z.string(),
  startSec: z.number(),
  durationSec: z.number(),
  partySize: z.number(),
  serviceDay: z.string(),
  languageCode: z.string(),
  specialRequests: z.string().nullable().optional(),
  allergyNote: z.string().nullable().optional(),
  version: z.number(),
  allowModify: z.boolean(),
  allowCancel: z.boolean(),
  past: z.boolean(),
  /** The instant self-service closes (epoch sec), or null when no cutoff is configured (BK-6). */
  cancellableUntilSec: z.number().nullable().optional(),
  withinCancellationWindow: z.boolean(),
  cancellationCutoffSeconds: z.number().nullable().optional(),
  policyText: z.string().nullable().optional(),
});
export type ManageBooking = z.infer<typeof manageBookingSchema>;

/** A withdraw-and-resubmit result: the NEW booking's manage link the page redirects to (mode switch / PENDING). */
const resubmitSchema = z.object({
  resubmitted: z.literal(true),
  venueId: z.string(),
  bookingId: z.string(),
  manageToken: z.string(),
  status: z.string(),
});
export type ResubmitResult = z.infer<typeof resubmitSchema>;

export type ModifyResult =
  | { kind: "modified"; booking: ManageBooking }
  | { kind: "resubmitted"; redirect: ResubmitResult };

export interface ModifyBookingBody {
  expectedVersion: number;
  idempotencyKey: string;
  serviceId?: string;
  startSec?: number;
  partySize?: number;
}

const waitlistJoinResponseSchema = z.object({
  confirmed: z.boolean(),
  externalEntryId: z.string().nullable().optional(),
});
export type WaitlistJoinResponse = z.infer<typeof waitlistJoinResponseSchema>;

export interface JoinWaitlistBody {
  serviceId: string;
  givenName?: string;
  familyName?: string;
  phone?: string;
  email?: string;
  partySize: number;
}

/**
 * Join the reservation waitlist (waiting-queues, §C.9) — the anonymous step the widget shows when
 * `openSlots` returns empty (after alternatives). The response is deliberately NEUTRAL (GU-4): a
 * blacklisted diner sees the same "you're on the list" confirmation, so the UI never branches on it.
 * One idempotency key per logical join (BK-2/§D.5), passed explicitly so a transport retry never
 * doubles the entry.
 */
export async function joinWaitlist(
  venueId: string,
  idempotencyKey: string,
  body: JoinWaitlistBody,
): Promise<WaitlistJoinResponse> {
  const res = await customInstance<unknown>(`${base(venueId)}/waitlist`, {
    method: "POST",
    headers: { "content-type": "application/json", "Idempotency-Key": idempotencyKey },
    body: JSON.stringify(body),
  });
  return waitlistJoinResponseSchema.parse(res);
}

export async function fetchManageBooking(venueId: string, token: string): Promise<ManageBooking> {
  const body = await customInstance<unknown>(
    `${base(venueId)}/manage/${encodeURIComponent(token)}`,
    { method: "GET" },
  );
  return manageBookingSchema.parse(body);
}

export async function modifyBooking(
  venueId: string,
  token: string,
  body: ModifyBookingBody,
): Promise<ModifyResult> {
  const { idempotencyKey, ...payload } = body;
  const res = await customInstance<unknown>(
    `${base(venueId)}/manage/${encodeURIComponent(token)}/modify`,
    {
      method: "POST",
      headers: { "content-type": "application/json", "Idempotency-Key": idempotencyKey },
      body: JSON.stringify(payload),
    },
  );
  if (res && typeof res === "object" && (res as { resubmitted?: unknown }).resubmitted === true) {
    return { kind: "resubmitted", redirect: resubmitSchema.parse(res) };
  }
  return { kind: "modified", booking: manageBookingSchema.parse(res) };
}

export async function cancelBooking(
  venueId: string,
  token: string,
  expectedVersion: number,
): Promise<ManageBooking> {
  const res = await customInstance<unknown>(
    `${base(venueId)}/manage/${encodeURIComponent(token)}/cancel`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ expectedVersion }),
    },
  );
  return manageBookingSchema.parse(res);
}

export function useManageBooking(venueId: string, token: string): UseQueryResult<ManageBooking> {
  return useQuery({
    queryKey: ["manage-booking", venueId, token],
    queryFn: () => fetchManageBooking(venueId, token),
    enabled: venueId.length > 0 && token.length > 0,
    // A manage-link fetch is a definitive answer (unknown/terminal token → 404): do not retry, so the
    // not-found state renders at once; the ErrorState offers a manual retry for a transient failure.
    retry: false,
    staleTime: 0,
  });
}

// --- typed rejection extraction (R7/R8/R5) ------------------------------------------------------

/** A booking/availability rejection surfaced at the HTTP edge as problem+json (§D.1). */
export interface BookingRejection {
  rejectionCode: string;
  alternatives: Alternative[];
  largestFitParty?: number;
  status: number;
}

/**
 * Pull the typed rejection out of a thrown `ApiError`. The problem body carries `rejectionCode` and,
 * for slot/covers rejections, the live-checked `alternatives` (AV-6) the widget re-offers with no
 * extra fetch. Returns null for a plain transport/validation failure (handled elsewhere).
 */
export function bookingRejectionOf(err: unknown, serviceDay: string): BookingRejection | null {
  if (!(err instanceof ApiError) || !err.problem) return null;
  const problem = err.problem as {
    rejectionCode?: string;
    alternatives?: Array<{ startSec: number; durationSec: number; serviceId?: string }>;
    largestFitParty?: number;
  };
  if (typeof problem.rejectionCode !== "string") return null;
  const alternatives: Alternative[] = (problem.alternatives ?? []).map((a) => ({
    serviceId: a.serviceId ?? "",
    serviceDay,
    startSec: a.startSec,
    durationSec: a.durationSec,
    spotsOpen: null,
  }));
  return {
    rejectionCode: problem.rejectionCode,
    alternatives,
    largestFitParty: problem.largestFitParty,
    status: err.status,
  };
}

// --- query hooks (reads) -------------------------------------------------------------------------

export function useOpenSlots(
  venueId: string,
  serviceDay: string | null,
  partySize: number,
  enabled: boolean,
): UseQueryResult<OpenSlotsResponse> {
  return useQuery({
    queryKey: ["open-slots", venueId, serviceDay, partySize],
    queryFn: () => fetchOpenSlots(venueId, serviceDay!, partySize),
    enabled: enabled && serviceDay !== null,
    retry: 1,
    staleTime: 0,
  });
}

export function useGroupAvailability(
  venueId: string,
  serviceDay: string | null,
  partySize: number,
  enabled: boolean,
): UseQueryResult<GroupAvailability> {
  return useQuery({
    queryKey: ["group-availability", venueId, serviceDay, partySize],
    queryFn: () => fetchGroupAvailability(venueId, serviceDay!, partySize),
    enabled: enabled && serviceDay !== null,
    retry: 1,
    staleTime: 0,
  });
}

/** The venue-config `BookingMode` mirrors the availability wire enum — one source, re-exported. */
export type { BookingMode };
